/**
 * 파일명 : evl0106m.jsx
 * 설명   : 프로젝트 평가 (팀원)
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0        2025-12-10    김준우    최초작성

 ************************************************
/**
 * Descriptions
 * 프로젝트 평가 시스템 - 팀원용 평가표 (evl0106m)
 *
 * 변경 사항:
 * 1. 프로젝트 평가(팀원) 신규 작성
 */
/************************************************
 * Import 영역
 ************************************************/

/* React */
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Select,
  Input,
  Button,
  Card,
  Typography,
  Form,
  Spin,
  message,
  Modal,
} from "antd";

/*  업무  */
import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";

/* Reducer */
import type {
  IPropsEvl0106M,
  CommonEvaluation,
  JobEvaluation,
} from "../../../redux/models/evl/evl0106m_model";

/* Render 변수 선언영역 */
/* AntD */
const { TextArea } = Input;
const { Title } = Typography;

export default function Evl0106m(props: IPropsEvl0106M) {
  const [form] = Form.useForm();

  // 최초 로딩 여부 추적
  const isInitialLoad = useRef<boolean>(true);

  // 검증 실패 시 스크롤 이동용 ref들
  const subSysRef = useRef<HTMLDivElement | null>(null);
  const commonTableRef = useRef<HTMLDivElement | null>(null);
  const jobTableRef = useRef<HTMLDivElement | null>(null);
  const commentsRef = useRef<HTMLDivElement | null>(null);

  // Modal 관리용 ref
  const confirmModalRefs = React.useRef<any[]>([]);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  const scrollAndError = (
    ref: React.RefObject<HTMLElement>,
    errorMsg: string,
  ) => {
    message.error({ content: errorMsg, duration: 3 });
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.current.focus?.();
      }
    }, 300);
  };

  const scrollToRowSelector = (selector: string) => {
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  };

  /************************************************
   * 전역변수 영역
   ************************************************/
  const evlEnableYn = props.evlEnableYn ?? "Y"; // Props에서 받은 편집 가능 여부 ('Y': 수정 가능, 'N': 읽기 전용)
  const isPreview = props.viewMode === "preview"; // 미리보기 모드 여부
  const isReadonly =
    props.viewMode === "readonly" || props.pjPmEmpNo !== common.getEmpNo(); //읽기 전용 모드
  const [loading, setLoading] = useState<boolean>(true);
  const [submitYn, setSubmitYn] = useState<string>("N"); // 제출 여부
  const [isWithinDeadline, setIsWithinDeadline] = useState<boolean>(false); // 마감일 이내 여부 (서버 응답)
  const [evlId, setEvlId] = useState<string>(""); // 프로젝트매핑ID
  const [projectCd, setProjectCd] = useState<string>(""); // 프로젝트코드
  const [urNm, setUrNm] = useState<string>(""); // 피평가자명
  const [dept, setDept] = useState<string>(""); // 부서
  const [pm, setPm] = useState<string>(""); // PM명
  const [pjtNm, setPjtNm] = useState<string>(""); // 프로젝트명
  const [subSysNm, setSubSysNm] = useState<string>(""); // Sub System명
  const [jobRole, setJobRole] = useState<string>("팀원"); // 직무
  const [pmEvlRmk, setPmEvlRmk] = useState<string>(""); // PM평가의견
  const [commonEvalData, setCommonEvalData] = useState<CommonEvaluation[]>([]); // 공통평가항목
  const [jobEvalData, setJobEvalData] = useState<JobEvaluation[]>([]); // 직무별평가항목

  // 선택한 직무에 따라 필터링된 평가 항목
  const filteredJobEvalData = jobEvalData.filter(
    (item) => item.jobType === jobRole,
  );

  // 입력 필드 비활성화 조건: evlEnableYn='N' 또는 submitYn='Y'
  const isDisabled = evlEnableYn === "N" || submitYn === "Y" || isPreview;

  /**
   * 기능  : 평가 데이터 조회
   * param :
   */
  const fetchEvaluationData = () => {
    setLoading(true);

    // 미리보기 모드에서는 메인페이지 접근 경고를 띄우지 않음
    if (isPreview) {
      setLoading(false);
    } else {
      // 평가메인화면에서 넘어왔는지 체크
      if (!props.evlId || !props.empNo) {
        openConfirmModal({
          title: "인사평가 메인페이지에서 접근하십시오.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } }, // 취소 버튼 숨김
          maskClosable: false, // 바깥 클릭 방지
          keyboard: false, // ESC 방지
          onOk() {
            // 인사평가 메인 페이지로 이동
            common.openPage({ menuId: "MEVL2025002" });
          },
          onCancel() {
            // 인사평가 메인 페이지로 이동
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
        return;
      }
    }

    // Props에서 받은 데이터로 요청
    const requestData = {
      evlId: props.evlId,
      empNo: props.empNo || common.getEmpNo(),
      deptCd: common.getDeptCd(),
      previewYn: isPreview ? "Y" : "N", // viewMode가 'preview'인 경우 previewYn='Y'로 전달
    };

    /**
     * 기능  : 조회
     * param :
     */
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0004",
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const memberData = data.EvlPjtPmItems || {};

        // 제출 여부 설정 (대소문자 구분 없이 처리)
        const submitYnValue = (responseData.submitYn || "N")
          .toString()
          .toUpperCase();
        setSubmitYn(submitYnValue);

        // 마감일 이내 여부 설정 (서버에서 계산한 값 사용)
        const isWithinDeadlineValue = responseData.isWithinDeadline === true || responseData.isWithinDeadline === "Y";
        setIsWithinDeadline(isWithinDeadlineValue);

        // 서버 응답 데이터의 모든 필드 확인
        if (memberData.urNm) {
          setEvlId(memberData.evlId || props.evlId || "");
          // 여러 가능한 필드명 시도
          setProjectCd(memberData.projectCd || "");
          setUrNm(memberData.urNm || "");
          setDept(memberData.deptNm || "");
          setPm(memberData.pmNm || "");
          setPjtNm(memberData.pjtNm || props.pjNm || "");
          setSubSysNm(memberData.subSysNm || "");

          // jobCd를 jobRole로 변환
          const convertedJobRole = memberData.jobCd
            ? getJobNameFromCode(memberData.jobCd)
            : memberData.jobRole || "팀원";
          setJobRole(convertedJobRole);

          setPmEvlRmk(memberData.pmEvlRmk || memberData.evlComment || ""); // pmEvlRmk 우선, evlComment 대체
        }

        const evlItems = isPreview ? memberData : memberData.evlItems || [];

        if (evlItems.length === 0) {
          setCommonEvalData([]);
          setJobEvalData([]);
          setLoading(false);
          return;
        }

        // dtlTypCd별로 그룹화
        const groupedByDtlTypCd: Record<string, any[]> = {};
        evlItems.forEach((item: any, idx: number) => {
          // hrEvlItmId의 5~6번째 문자열이 dtlTypCd
          const hrEvlItmId =
            item.evlItemId || item.hrEvlItmId || item.evlItmId || "";
          const dtlTypCd =
            hrEvlItmId.length >= 6 ? hrEvlItmId.substring(4, 6) : "unknown";

          if (!groupedByDtlTypCd[dtlTypCd]) {
            groupedByDtlTypCd[dtlTypCd] = [];
          }
          groupedByDtlTypCd[dtlTypCd].push(item);
        });

        // 공통 평가 항목 (dtlTypCd === '17')
        const commonItems: CommonEvaluation[] = [];
        if (groupedByDtlTypCd["17"]) {
          groupedByDtlTypCd["17"].forEach((item: any, index: number) => {
            const commonItem = {
              key: `c${index + 1}`,
              hrEvlItmId: item.evlItemId || item.hrEvlItmId || item.evlItmId,
              category: item.itmNm || "공통",
              item: `${index + 1}. ${item.desc1 || item.itmDesc || ""}`,
              score: Number(item.baseAsgn || 0),
              pmGrade: item.pmEvlScr || item.pmGrade || null,
            };
            commonItems.push(commonItem);
          });

          // 소계 추가
          const commonTotal = commonItems.reduce(
            (sum, itm) => sum + itm.score,
            0,
          );
          const subtotal: CommonEvaluation = {
            key: `c${commonItems.length + 1}`,
            category: commonItems[0]?.category || "공통",
            item: "소계",
            score: commonTotal,
            pmGrade: null,
          };
          commonItems.push(subtotal);
        }
        setCommonEvalData(commonItems);

        // 직무별 평가 항목 (dtlTypCd !== '17')
        const jobItems: JobEvaluation[] = [];
        const jobDtlTypCds = Object.keys(groupedByDtlTypCd).filter(
          (dtlTypCd) => dtlTypCd !== "17",
        );

        jobDtlTypCds.forEach((dtlTypCd) => {
          const items = groupedByDtlTypCd[dtlTypCd];
          const jobTypeName = items[0]?.itmNm || `직무${dtlTypCd}`;

          items.forEach((item: any, index: number) => {
            const jobItem = {
              key: `${dtlTypCd}-${index + 1}`,
              hrEvlItmId: item.evlItemId || item.hrEvlItmId || item.evlItmId,
              jobType: jobTypeName,
              item: `${index + 1}. ${item.desc1 || item.itmDesc || ""}`,
              score: Number(item.baseAsgn || 0),
              pmGrade: item.pmEvlScr || item.pmGrade || null,
            };
            jobItems.push(jobItem);
          });

          // 소계 추가
          const jobTotal = items.reduce(
            (sum: number, itm: any) => sum + Number(itm.baseAsgn || 0),
            0,
          );
          const subtotal: JobEvaluation = {
            key: `${dtlTypCd}-subtotal`,
            jobType: jobTypeName,
            item: "소계",
            score: jobTotal,
            pmGrade: null,
          };
          jobItems.push(subtotal);
        });

        setJobEvalData(jobItems);

        if (isInitialLoad.current && !isPreview) {
          message.success("프로젝트 평가내용 조회완료");
          isInitialLoad.current = false;
        }
        setLoading(false);
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || "데이터를 불러오는 중 오류가 발생했습니다.");
        setLoading(false);
      },
    });
  };

  /**
   * 기능  : 데이터 로드 (최초 1회)
   * param :
   */
  useEffect(() => {
    fetchEvaluationData();
  }, []);

  /**
   * 기능  : 브라우저 뒤로가기 시 Modal 닫기
   * param :
   */
  useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  /**
   * 기능  : 점수 계산 함수
   * param :
   */
  const getGradeMultiplier = (grade: string | null): number => {
    switch (grade) {
      case "A":
        return 1.0;
      case "B":
        return 0.75;
      case "C":
        return 0.5;
      case "D":
        return 0.25;
      default:
        return 0;
    }
  };

  /**
   * 기능  : 직무를 JOB_CD로 변환하는 함수
   * param :
   */
  const getJobCode = (jobRole: string): string => {
    switch (jobRole) {
      case "M/M":
        return "01";
      case "팀원":
        return "02";
      case "컨설턴트":
        return "03";
      default:
        return "02"; // 기본값 팀원
    }
  };

  /**
   * 기능  : JOB_CD를 직무명으로 변환하는 함수
   * param :
   */
  const getJobNameFromCode = (jobCd: string): string => {
    switch (jobCd) {
      case "01":
        return "M/M";
      case "02":
        return "팀원";
      case "03":
        return "컨설턴트";
      default:
        return "팀원"; // 기본값 팀원
    }
  };

  /**
   * 기능  : 점수 집계 함수
   * param :
   */
  const calculateCommonPmScore = (): number => {
    return commonEvalData
      .filter((item) => item.item !== "계" && item.item !== "소계")
      .reduce(
        (sum, item) => sum + item.score * getGradeMultiplier(item.pmGrade),
        0,
      );
  };

  const calculateJobPmScore = (jobType: string): number => {
    return jobEvalData
      .filter(
        (item) =>
          item.jobType === jobType &&
          item.item !== "계" &&
          item.item !== "소계",
      )
      .reduce(
        (sum, item) => sum + item.score * getGradeMultiplier(item.pmGrade),
        0,
      );
  };

  /**
   * 기능  : 공통 평가 테이블 컬럼
   * param :
   */
  const commonEvalColumns = [
    {
      title: "구분",
      dataIndex: "category",
      key: "category",
      align: "center" as const,
      width: "10%",
      onCell: (record: CommonEvaluation, index?: number) => {
        // 첫 번째 행에만 rowSpan 적용 (공통 평가 데이터 기준)
        if (index === 0) {
          return { rowSpan: commonEvalData.length };
        }
        return { rowSpan: 0 };
      },
    },
    {
      title: "평가항목",
      dataIndex: "item",
      key: "item",
      align: "left" as const,
      width: "70%",
      onCell: (record: CommonEvaluation) => {
        if (record.item === "계" || record.item === "소계") {
          return {
            style: {
              textAlign: "center" as const,
              fontWeight: "bold" as const,
            },
          };
        }
        return {};
      },
    },
    {
      title: "배점",
      dataIndex: "score",
      key: "score",
      align: "center" as const,
      width: "10%",
      onCell: (record: CommonEvaluation) => {
        if (record.item === "계" || record.item === "소계") {
          return { style: { fontWeight: "bold" as const } };
        }
        return {};
      },
    },
    {
      title: "PM",
      dataIndex: "pmGrade",
      key: "pmGrade",
      align: "center" as const,
      width: "10%",
      render: (value: string | null, record: CommonEvaluation) => {
        if (record.item === "계" || record.item === "소계") {
          return (
            <span style={{ fontWeight: "bold" }}>
              {calculateCommonPmScore().toFixed(2)}
            </span>
          );
        }
        return (
          <Select
            className="center-select"
            value={value}
            onChange={(grade) => {
              const newData = commonEvalData.map((item) =>
                item.key === record.key ? { ...item, pmGrade: grade } : item,
              );
              setCommonEvalData(newData);
            }}
            placeholder="선택"
            allowClear
            disabled={isDisabled}
            style={{ width: "100%" }}
            options={[
              { value: "A", label: "A" },
              { value: "B", label: "B" },
              { value: "C", label: "C" },
              { value: "D", label: "D" },
            ]}
          />
        );
      },
    },
  ];

  /**
   * 기능  : 직무별 평가 테이블 컬럼
   * param :
   */
  const jobEvalColumns = [
    {
      title: "구분",
      dataIndex: "jobType",
      key: "jobType",
      align: "center" as const,
      width: "10%",
      onCell: (record: JobEvaluation, index?: number) => {
        // 첫 번째 행에만 rowSpan 적용 (필터링된 데이터 기준)
        if (index === 0) {
          return { rowSpan: filteredJobEvalData.length };
        }
        return { rowSpan: 0 };
      },
    },
    {
      title: "평가항목",
      dataIndex: "item",
      key: "item",
      align: "left" as const,
      width: "70%",
      onCell: (record: JobEvaluation) => {
        if (record.item === "계" || record.item === "소계") {
          return {
            style: {
              textAlign: "center" as const,
              fontWeight: "bold" as const,
            },
          };
        }
        return {};
      },
    },
    {
      title: "배점",
      dataIndex: "score",
      key: "score",
      align: "center" as const,
      width: "10%",
      onCell: (record: JobEvaluation) => {
        if (record.item === "계" || record.item === "소계") {
          return { style: { fontWeight: "bold" as const } };
        }
        return {};
      },
    },
    {
      title: "PM",
      dataIndex: "pmGrade",
      key: "pmGrade",
      align: "center" as const,
      width: "10%",
      render: (value: string | null, record: JobEvaluation) => {
        if (record.item === "계" || record.item === "소계") {
          return (
            <span style={{ fontWeight: "bold" }}>
              {calculateJobPmScore(record.jobType).toFixed(2)}
            </span>
          );
        }
        return (
          <Select
            className="center-select"
            value={value}
            onChange={(grade) => {
              const newData = jobEvalData.map((item) =>
                item.key === record.key ? { ...item, pmGrade: grade } : item,
              );
              setJobEvalData(newData);
            }}
            placeholder="선택"
            allowClear
            disabled={isDisabled}
            style={{ width: "100%" }}
            options={[
              { value: "A", label: "A" },
              { value: "B", label: "B" },
              { value: "C", label: "C" },
              { value: "D", label: "D" },
            ]}
          />
        );
      },
    },
  ];

  /**
   * 기능  : handleCancelSubmit
   * 설명  : 제출취소 버튼 클릭 시 실행되는 함수
   */
  const handleCancelSubmit = () => {
    openConfirmModal({
      title: "제출을 취소하시겠습니까?",
      content: "제출취소 후 기한 내에 다시 제출할 수 있습니다.",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        performCancelSubmit();
      },
    });
  };

  /**
   * 기능  : performCancelSubmit
   * 설명  : 실제 제출취소 로직 실행 (submitYn='N'으로 제출)
   *         성공 시 "제출취소 되었습니다." 알림 후 인사평가 메인으로 이동
   *         실패 시 에러 메시지 Modal 표시
   */
  const performCancelSubmit = () => {
    const evlYear = evlId ? evlId.substring(0, 4) : "";

    const requestData = {
      evlId: evlId,
      evlYear: evlYear,
      empNo: common.getEmpNo(),
      evlCd: "04",
      submitYn: "N",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0006",
      success: (responseData: any) => {
        const resultCode =
          responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg =
          responseData?.data?.resultMsg || responseData?.resultMsg;

        if (resultCode === "400") {
          openConfirmModal({
            title: "제출취소 실패",
            content: resultMsg || "제출취소에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          message.success("제출취소 되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (errorMsg: string) => {
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출취소에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * 기능  : 저장 버튼 핸들러
   * param :
   */
  const handleSave = () => {
    if (pmEvlRmk.length > 500) {
      scrollAndError(
        commentsRef,
        `기타 평가의견은 500자 이하로 입력해주세요. (현재 ${pmEvlRmk.length}자)`,
      );
      return;
    }

    openConfirmModal({
      title: "저장하시겠습니까?",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        handleSaveLogic();
      },
      onCancel: () => {
        message.info({
          content: "저장 취소",
          duration: 2,
        });
      },
    });
  };

  const handleSaveLogic = () => {
    /**
     * 기능  : 직무 코드 변환 함수 호출
     * param :
     */
    const jobCode = getJobCode(jobRole);

    /**
     * 기능  : 현재 선택된 직무에 해당하는 평가 항목만 필터링 (소계 제외)
     * param :
     */
    const filteredJobEvalItems = jobEvalData.filter(
      (item) => item.item !== "소계" && item.jobType === jobRole,
    );

    /**
     * 기능  : 총점 계산 함수 호출
     * param :
     */
    const totalScore = calculateCommonPmScore() + calculateJobPmScore(jobRole);

    // 평가년도 추출 (evlId의 앞 4자리)
    const evlYear = evlId ? evlId.substring(0, 4) : "";

    /**
     * 기능  : 저장할 데이터 세팅
     * param :
     */
    const saveData = {
      evlId: evlId, // 프로젝트매핑ID
      evlYear: evlYear, // 평가년도
      projectCd: projectCd, // 프로젝트코드
      empNo: common.getEmpNo(), // 사원번호
      urNm: urNm, // 피평가자명
      dept: dept, // 부서명
      pm: pm, // PM명
      pjtNm: pjtNm, // 프로젝트명
      subSysNm: subSysNm, // Sub System명
      evlCd: "04", // 평가코드
      jobCd: jobCode, // 직무코드
      jobRole: jobRole,
      pmEvlRmk: pmEvlRmk, // PM평가의견
      pmEvlTotScr: totalScore, // 총점
      commonEvalItems: commonEvalData.filter((item) => item.item !== "소계"),
      jobEvalItems: filteredJobEvalItems, // 선택된 직무만 전송
      submitYn: "N", // 제출여부
    };

    /**
     * 기능  : 저장
     * param :
     */
    commonProxy.callAsyncSvc(saveData, {
      txCd: "TREVLS0005",
      success: (responseData: any) => {
        // 응답 데이터에서 resultCode와 resultMsg 추출
        const resultCode =
          responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg =
          responseData?.data?.resultMsg || responseData?.resultMsg;

        // resultCode에 따른 처리
        if (resultCode === "200" || resultCode === "0" || !resultCode) {
          // 성공
          message.success("저장되었습니다.");
          // 저장 성공 시 자동 재조회
          fetchEvaluationData();
        } else if (resultCode === "400") {
          // 이미 제출된 평가 등의 에러
          openConfirmModal({
            title: "저장 실패",
            content: resultMsg || "저장에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          // 기타 에러 (500 등)
          openConfirmModal({
            title: "오류",
            content: resultMsg || "저장 중 오류가 발생했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        }
      },

      error: (errorMsg: string) => {
        message.error(errorMsg || "저장 중 오류가 발생했습니다.");
      },
    });
  };

  /**
   * 기능  : 제출 버튼 핸들러
   * param :
   */
  const handleSubmit = () => {
    // Sub System 필수 체크 추가
    if (!subSysNm || subSysNm.trim() === "") {
      scrollAndError(subSysRef, "Sub System을 입력해주세요.");
      return;
    }

    const unevaluatedCommon = commonEvalData.filter(
      (item) => item.item !== "계" && item.item !== "소계" && !item.pmGrade,
    );

    /**
     * 기능  : 제출 버튼 클릭 시 미평가 항목 체크
     * param :
     */
    if (unevaluatedCommon.length > 0) {
      message.error({
        content: `공통 평가 항목 중 평가하지 않은 항목이 ${unevaluatedCommon.length}개 있습니다.`,
        duration: 3,
      });
      const firstCommon = unevaluatedCommon[0];
      if (firstCommon) {
        scrollToRowSelector(`[data-common-key="${firstCommon.key}"]`);
      } else {
        scrollToRowSelector("[data-common-table='true']");
      }
      return;
    }

    const unevaluatedJob = jobEvalData.filter(
      (item) =>
        item.jobType === jobRole &&
        item.item !== "계" &&
        item.item !== "소계" &&
        !item.pmGrade,
    );

    if (unevaluatedJob.length > 0) {
      message.error({
        content: `${jobRole} 평가 항목 중 평가하지 않은 항목이 ${unevaluatedJob.length}개 있습니다.`,
        duration: 3,
      });
      const firstJob = unevaluatedJob[0];
      if (firstJob) {
        scrollToRowSelector(`[data-job-key="${firstJob.key}"]`);
      } else {
        scrollToRowSelector("[data-job-table='true']");
      }
      return;
    }

    if (!pmEvlRmk || pmEvlRmk.trim() === "") {
      scrollAndError(commentsRef, "기타 평가 의견을 입력해주세요.");
      return;
    }

    if (pmEvlRmk.length > 500) {
      scrollAndError(
        commentsRef,
        `기타 평가의견은 500자 이하로 입력해주세요. (현재 ${pmEvlRmk.length}자)`,
      );
      return;
    }

    openConfirmModal({
      title: "제출하시겠습니까?",
      content: "제출완료 시 수정이 불가합니다.",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        handleSubmitLogic();
      },
      onCancel: () => {
        message.info({
          content: "제출 취소",
          duration: 2,
        });
      },
    });
  };

  const handleSubmitLogic = () => {
    /**
     * 기능  : 직무 코드 변환 함수 호출
     * param :
     */
    const jobCode = getJobCode(jobRole);

    /**
     * 기능  : 현재 선택된 직무에 해당하는 평가 항목 필터링
     * param :
     */
    const filteredJobEvalItems = jobEvalData.filter(
      (item) => item.item !== "소계" && item.jobType === jobRole,
    );

    /**
     * 기능  : 총점 계산 함수 호출
     * param :
     */
    const totalScore = calculateCommonPmScore() + calculateJobPmScore(jobRole);

    // 평가년도 추출 (evlId의 앞 4자리)
    const evlYear = evlId ? evlId.substring(0, 4) : "";

    /**
     * 기능  : 제출할 데이터 세팅
     * param :
     */
    const submitData = {
      evlId: evlId, // 프로젝트매핑ID
      evlYear: evlYear, // 평가년도
      projectCd: projectCd, // 프로젝트코드
      empNo: common.getEmpNo(), // 사원번호
      urNm: urNm, // 피평가자명
      dept: dept, // 부서명
      pm: pm, // PM명
      pjtNm: pjtNm, // 프로젝트명
      subSysNm: subSysNm, // Sub System명
      evlCd: "04", // 평가코드
      jobCd: jobCode, // 직무코드
      jobRole: jobRole, // 직무명
      pmEvlRmk: pmEvlRmk, // PM평가의견
      pmEvlTotScr: totalScore, // 총점
      commonEvalItems: commonEvalData.filter((item) => item.item !== "소계"),
      jobEvalItems: filteredJobEvalItems, // 선택된 직무만 전송
      submitYn: "Y", // 제출여부
    };

    /**
     * 기능  : 제출
     * param :
     */
    commonProxy.callAsyncSvc(submitData, {
      txCd: "TREVLS0006",
      success: (responseData: any) => {
        const resultCode =
          responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg =
          responseData?.data?.resultMsg || responseData?.resultMsg;

        if (resultCode === "400") {
          openConfirmModal({
            title: "제출 실패",
            content: resultMsg || "이미 기 제출된 평가입니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else if (resultCode === "200" || resultCode === "0" || !resultCode) {
          message.success("제출되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        } else {
          openConfirmModal({
            title: "오류",
            content: resultMsg || "제출 중 오류가 발생했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        }
      },
      error: (errorMsg: string) => {
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  // 로딩 화면
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <Spin size="large">
          <div style={{ padding: 50 }} />
        </Spin>
        <div style={{ marginTop: 16, color: "#666" }}>
          데이터를 불러오는 중입니다...
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "1200px",
        margin: "0 auto",
        /* fontFamily: '"맑은 고딕", "Malgun Gothic", sans-serif', */
      }}
    >
      <Title
        level={2}
        style={{
          textAlign: "center",
          marginBottom: "24px",
        }}
      >
        프로젝트 평가 (팀원)
      </Title>

      <Form form={form} layout="vertical">
        {/* 기본 정보 */}
        <Card
          style={{ marginBottom: "24px" }}
          styles={{ body: { padding: "16px" } }}
        >
          <Table
            dataSource={[
              {
                key: "1",
                label1: "이 름",
                value1: urNm,
                label2: "소 속",
                value2: dept,
                label3: "P M",
                value3: pm,
              },
              {
                key: "2",
                label1: "프로젝트명",
                value1: pjtNm,
                label2: "",
                value2: "",
                label3: "",
                value3: "",
              },
              {
                key: "3",
                label1: "* Sub System",
                value1: subSysNm, // subSys → subSysNm
                label2: "",
                value2: "",
                label3: "* 직 무",
                value3: jobRole,
              },
            ]}
            columns={[
              {
                title: "label1",
                dataIndex: "label1",
                key: "label1",
                width: 100,
                align: "center" as const,
                render: (text) => {
                  // * 표시가 있는 경우 빨간색으로 강조
                  if (text && text.startsWith("*")) {
                    return <span style={{ color: "#ff4d4f" }}>{text}</span>;
                  }
                  return text;
                },
              },
              {
                dataIndex: "value1",
                key: "value1",
                width: 300,
                render: (text, record) => {
                  if (record.key === "1") {
                    return (
                      <Input
                        value={urNm}
                        onChange={(e) => setUrNm(e.target.value)}
                        placeholder=""
                        variant="borderless"
                        readOnly
                        style={{
                          padding: "4px 11px",
                          color: "rgb(0, 0, 0)",
                          fontWeight: "500",
                          textAlign: "center",
                        }}
                      />
                    );
                  }
                  if (record.key === "2") {
                    return (
                      <Input
                        value={pjtNm}
                        onChange={(e) => setPjtNm(e.target.value)}
                        placeholder=""
                        variant="borderless"
                        readOnly
                        style={{
                          padding: "4px 11px",
                          color: "rgb(0, 0, 0)",
                          fontWeight: "500",
                        }}
                      />
                    );
                  }
                  if (record.key === "3") {
                    return (
                      <div ref={subSysRef}>
                        <Input
                          value={subSysNm} // subSys → subSysNm
                          onChange={(e) => setSubSysNm(e.target.value)}
                          placeholder="Sub System을 입력하세요"
                          variant="borderless"
                          maxLength={100}
                          disabled={isDisabled}
                          style={{
                            padding: "4px 11px",
                            ...(isDisabled && {
                              backgroundColor: "#f5f5f5",
                              color: "rgb(0, 0, 0)",
                            }),
                          }}
                        />
                      </div>
                    );
                  }
                  return null;
                },
                onCell: (record) => {
                  if (record.key === "2") {
                    return { colSpan: 5 }; // 프로젝트명은 전체 행에 병합
                  }
                  if (record.key === "3") {
                    return { colSpan: 3 };
                  }
                  return {};
                },
              },
              {
                dataIndex: "label2",
                key: "label2",
                width: 100,
                align: "center" as const,
                onCell: (record) => {
                  if (record.key === "2") {
                    return { colSpan: 0 }; // 프로젝트명 행에서는 숨김
                  }
                  if (record.key === "3") {
                    return { colSpan: 0 };
                  }
                  return {};
                },
              },
              {
                dataIndex: "value2",
                key: "value2",
                width: 150,
                render: (text, record) => {
                  if (record.key === "1") {
                    return (
                      <Input
                        value={dept}
                        onChange={(e) => setDept(e.target.value)}
                        placeholder=""
                        variant="borderless"
                        readOnly
                        style={{
                          padding: "4px 11px",
                          color: "rgb(0, 0, 0)",
                          fontWeight: "500",
                          textAlign: "center",
                        }}
                      />
                    );
                  }
                  return null;
                },
                onCell: (record) => {
                  if (record.key === "2") {
                    return { colSpan: 0 }; // 프로젝트명 행에서는 숨김
                  }
                  if (record.key === "3") {
                    return { colSpan: 0 };
                  }
                  return {};
                },
              },
              {
                dataIndex: "label3",
                key: "label3",
                width: 100,
                align: "center" as const,
                render: (text) => {
                  // * 표시가 있는 경우 빨간색으로 강조
                  if (text && text.startsWith("*")) {
                    return <span style={{ color: "#ff4d4f" }}>{text}</span>;
                  }
                  return text;
                },
                onCell: (record) => {
                  if (record.key === "2") {
                    return { colSpan: 0 }; // 프로젝트명 행에서는 숨김
                  }
                  return {};
                },
              },
              {
                dataIndex: "value3",
                key: "value3",
                width: 150,
                render: (text, record) => {
                  if (record.key === "1") {
                    return (
                      <Input
                        value={pm}
                        onChange={(e) => setPm(e.target.value)}
                        placeholder=""
                        variant="borderless"
                        readOnly
                        style={{
                          padding: "4px 11px",
                          color: "rgb(0, 0, 0)",
                          fontWeight: "500",
                          textAlign: "center",
                        }}
                      />
                    );
                  }
                  if (record.key === "3") {
                    return (
                      <Select
                        value={jobRole}
                        onChange={(value) => setJobRole(value)}
                        variant="borderless"
                        disabled={evlEnableYn === "N" || submitYn === "Y"}
                        style={{
                          width: "100%",
                          textAlign: "center",
                        }}
                        options={[
                          { value: "팀원", label: "팀원" },
                          { value: "M/M", label: "M/M" },
                          { value: "컨설턴트", label: "컨설턴트" },
                        ]}
                      />
                    );
                  }
                  return null;
                },
                onCell: (record) => {
                  if (record.key === "2") {
                    return { colSpan: 0 }; // 프로젝트명 행에서는 숨김
                  }
                  return {};
                },
              },
            ]}
            pagination={false}
            bordered
            size="small"
            showHeader={false}
          />
        </Card>

        {/* 평가 항목 */}
        <Title level={4} style={{ marginBottom: "16px" }}>
          평가 항목
        </Title>

        <Card
          style={{ marginBottom: "24px", borderRadius: 8 }}
          bodyStyle={{ padding: 16 }}
        >
          <div
            style={{
              marginBottom: "12px",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              borderRadius: 6,
            }}
          >
            <span>
              평가방법: A (우수), B (양호), C (보통), D (미흡)의 4단계로 평가
            </span>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <div ref={commonTableRef} data-common-table="true">
              <Table
                key={`common-table-${commonEvalData.length}`}
                columns={commonEvalColumns}
                dataSource={commonEvalData}
                pagination={false}
                bordered
                size="small"
                onRow={(record) =>
                  ({
                    "data-common-key": record.key,
                  }) as React.HTMLAttributes<HTMLElement>
                }
              />
            </div>
          </div>

          <div ref={jobTableRef} data-job-table="true">
            <Table
              key={`job-table-${jobRole}-${filteredJobEvalData.length}`}
              columns={jobEvalColumns}
              dataSource={filteredJobEvalData}
              pagination={false}
              bordered
              size="small"
              onRow={(record) =>
                ({
                  "data-job-key": record.key,
                }) as React.HTMLAttributes<HTMLElement>
              }
            />
          </div>
        </Card>

        {/* 점수 요약 */}
        <div style={{ marginBottom: "24px" }}>
          <Table
            key={`summary-${jobRole}-${JSON.stringify(jobEvalData.map((d) => ({ k: d.key, pm: d.pmGrade })))}`}
            dataSource={[
              {
                key: "1",
                category: "총계",
                common: calculateCommonPmScore().toFixed(2),
                job: calculateJobPmScore(jobRole).toFixed(2),
                total: (
                  calculateCommonPmScore() + calculateJobPmScore(jobRole)
                ).toFixed(2),
              },
            ]}
            columns={[
              {
                title: "구분",
                dataIndex: "category",
                key: "category",
                align: "center" as const,
                width: 80,
              },
              {
                title: "공통",
                dataIndex: "common",
                key: "common",
                align: "center" as const,
                width: 80,
              },
              {
                title: "직무별",
                dataIndex: "job",
                key: "job",
                align: "center" as const,
                width: 80,
              },
              {
                title: "합계",
                dataIndex: "total",
                key: "total",
                align: "center" as const,
                width: 80,
              },
            ]}
            pagination={false}
            bordered
            size="small"
          />
        </div>

        <style>{`
          .center-select .ant-select-selector {
            text-align: center !important;
          }
          .center-select .ant-select-selection-item {
            text-align: center !important;
            justify-content: center !important;
            display: flex !important;
          }
          .center-select .ant-select-selection-placeholder {
            text-align: center !important;
            justify-content: center !important;
            display: flex !important;
          }
          .ant-select-item-option-content {
            text-align: center !important;
            justify-content: center !important;
            display: flex !important;
          }
          .ant-table-thead th {
            text-align: center !important;
          }
        `}</style>

        {/* 평가 의견 */}
        <Title level={4} style={{ marginBottom: "16px" }}>
          기타 평가 의견
        </Title>

        <Form.Item>
          <div ref={commentsRef}>
            <TextArea
              value={pmEvlRmk} // evlComment → pmEvlRmk
              onChange={(e) => setPmEvlRmk(e.target.value)}
              placeholder=""
              rows={8}
              disabled={isDisabled}
              maxLength={500}
              showCount
            />
          </div>
        </Form.Item>

        {/* 버튼 영역 - 미리보기/읽기전용 모드에서는 전체 숨김 */}
        {!isPreview && !isReadonly && (
          <>
            {submitYn !== "Y" && evlEnableYn === "Y" && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <Button onClick={handleSave}>저장</Button>
                <Button type="primary" onClick={handleSubmit}>
                  제출
                </Button>
              </div>
            )}
            {submitYn === "Y" && isWithinDeadline && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <Button danger onClick={handleCancelSubmit}>
                  제출취소
                </Button>
              </div>
            )}
          </>
        )}
      </Form>
    </div>
  );
}
